import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Phone, Mail, Navigation, CalendarPlus, Pencil, Trash2, Send, XCircle, Trophy, RotateCcw, MapPin, Tag, Sparkles, Ruler, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { salesApi, apiError } from '../salesApi';
import { useT, fmtMoney, fmtDate, fmtDateTime } from '../salesI18n';
import { StatusBadge, VisitTypeBadge } from '../components/StatusBadge';
import { StatusStepper } from '../components/StatusStepper';
import { Timeline } from '../components/Timeline';
import { LeadFormDialog } from '../components/LeadFormDialog';
import { VisitFormDialog } from '../components/VisitFormDialog';
import { SimulationPanel } from '../components/simulation/SimulationPanel';
import { MeasurementSheet } from '../components/measurement/MeasurementSheet';
import { ProposalPanel } from '../components/proposal/ProposalPanel';
import { StudioPlanCard } from '../components/StudioPlanCard';
import { useSalesNotifications } from '../hooks/useSalesNotifications';

const mapsUrl = (addr) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr || '')}`;

const Info = ({ label, value, mono }) => (
  <div>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className={`text-sm mt-0.5 ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
  </div>
);

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { t, lang } = useT();
  const { leadsWithNew, running } = useSalesNotifications();
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [visitOpen, setVisitOpen] = useState(false);
  const [editVisit, setEditVisit] = useState(null);
  const [lostOpen, setLostOpen] = useState(false);
  const [lostReason, setLostReason] = useState('');

  const load = useCallback(async () => {
    try { const { data } = await salesApi.get(`/leads/${id}`); setLead(data); } catch (e) { setLead(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status, reason) => {
    try {
      await salesApi.patch(`/leads/${id}/status`, { status, lost_reason: reason || null });
      toast.success(t.statusUpdated);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try { await salesApi.post(`/leads/${id}/notes`, { text: note }); setNote(''); toast.success(t.noteAdded); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const deleteNote = async (entry) => {
    try { await salesApi.delete(`/leads/${id}/notes/${entry.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const deleteLead = async () => {
    if (!window.confirm(t.deleteLeadConfirm)) return;
    try { await salesApi.delete(`/leads/${id}`); toast.success(t.leadDeleted); navigate('/leads'); } catch (e) { toast.error(apiError(e)); }
  };

  const setVisitStatus = async (v, status) => {
    try { await salesApi.put(`/visits/${v.id}`, { status }); toast.success(t.visitSaved); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const deleteVisit = async (v) => {
    if (!window.confirm(t.deleteVisitConfirm)) return;
    try { await salesApi.delete(`/visits/${v.id}`); toast.success(t.visitDeleted); load(); } catch (e) { toast.error(apiError(e)); }
  };

  if (lead === null) return <p className="text-sm text-muted-foreground" data-testid="lead-loading">{t.loading}</p>;
  if (lead === false) return <div className="space-y-3"><p data-testid="lead-not-found">{t.notFound}</p><Link to="/leads" className="text-primary text-sm font-semibold">← {t.back}</Link></div>;

  const addr = lead.formatted_address || [lead.address, lead.city, lead.state, lead.zip_code].filter(Boolean).join(', ');

  return (
    <div className="space-y-5" data-testid="lead-detail-page">
      <Link to="/leads" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="lead-back-link"><ArrowLeft className="h-4 w-4" /> {t.leads}</Link>

      <div className="bg-card rounded-xl border border-border p-4 sm:p-6 shadow-sm space-y-4 sales-rise">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="lead-name">{lead.customer_name}</h1>
              <StatusBadge status={lead.status} testId="lead-detail-status" />
            </div>
            {addr && <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {addr}</p>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="h-10" onClick={() => setEditOpen(true)} data-testid="lead-edit-btn"><Pencil className="h-4 w-4 mr-1.5" /> {t.edit}</Button>
            <Button variant="outline" className="h-10 text-destructive hover:text-destructive" onClick={deleteLead} data-testid="lead-delete-btn"><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>

        <StatusStepper status={lead.status} onChange={(s) => changeStatus(s)} />

        <div className="flex flex-wrap gap-2" data-testid="lead-quick-actions">
          {lead.phone && <a href={`tel:${lead.phone}`} data-testid="lead-action-call" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-teal-200 bg-white/70 text-sm font-medium text-teal-800 hover:bg-teal-50 transition-colors"><Phone className="h-4 w-4" /> {t.call}</a>}
          {lead.email && <a href={`mailto:${lead.email}`} data-testid="lead-action-email" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-teal-200 bg-white/70 text-sm font-medium text-teal-800 hover:bg-teal-50 transition-colors"><Mail className="h-4 w-4" /> {t.sendEmail}</a>}
          {addr && <a href={mapsUrl(addr)} target="_blank" rel="noreferrer" data-testid="lead-action-navigate" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-teal-200 bg-white/70 text-sm font-medium text-teal-800 hover:bg-teal-50 transition-colors"><Navigation className="h-4 w-4" /> {t.navigate}</a>}
          <Button className="h-10 font-semibold" onClick={() => { setEditVisit(null); setVisitOpen(true); }} data-testid="lead-action-schedule-visit"><CalendarPlus className="h-4 w-4 mr-1.5" /> {t.scheduleVisit}</Button>
          <div className="ml-auto flex flex-wrap gap-2 max-w-full">
            {lead.status !== 'won' && lead.status !== 'lost' && (
              <>
                <Button variant="outline" className="h-10 text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => changeStatus('won')} data-testid="lead-action-won"><Trophy className="h-4 w-4 mr-1.5" /> {t.markWon}</Button>
                <Button variant="outline" className="h-10 text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => setLostOpen(true)} data-testid="lead-action-lost"><XCircle className="h-4 w-4 mr-1.5" /> {t.markLost}</Button>
              </>
            )}
            {(lead.status === 'won' || lead.status === 'lost') && (
              <Button variant="outline" className="h-10" onClick={() => changeStatus('measured')} data-testid="lead-action-reopen"><RotateCcw className="h-4 w-4 mr-1.5" /> {t.reopen}</Button>
            )}
          </div>
        </div>
      </div>

      {lead.studio_stage && <StudioPlanCard lead={lead} onOpenSimulation={() => { const p = new URLSearchParams(params); p.set('tab', 'simulation'); setParams(p, { replace: true }); document.querySelector('[data-testid="lead-tab-simulation"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} />}

      <div className="grid lg:grid-cols-[1fr_340px] gap-4 sm:gap-6">
        <Tabs value={params.get('tab') || 'simulation'} onValueChange={(v) => { const p = new URLSearchParams(params); p.set('tab', v); setParams(p, { replace: true }); }} className="bg-card rounded-xl border border-border shadow-sm min-w-0 overflow-hidden">
          <TabsList className="w-full justify-start rounded-none border-b border-slate-200/60 bg-transparent h-14 px-3 gap-1 overflow-x-auto sales-kanban-scroll">
            <TabsTrigger value="simulation" data-testid="lead-tab-simulation" className="h-9 px-4 gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> {t.simulation}
              {leadsWithNew.includes(id) && <span className="ml-1 rounded-full bg-white text-teal-700 text-[9px] font-bold px-1.5 py-0.5 uppercase" data-testid="lead-tab-simulation-new">{t.newBadge}</span>}
              {running.some((j) => j.lead_id === id) && <span className="ml-1 h-2 w-2 rounded-full bg-amber-500 animate-pulse" data-testid="lead-tab-simulation-running" />}
            </TabsTrigger>
            <TabsTrigger value="measurement" data-testid="lead-tab-measurement" className="h-9 px-4 gap-1.5"><Ruler className="h-3.5 w-3.5" /> {t.measurement}</TabsTrigger>
            <TabsTrigger value="proposal" data-testid="lead-tab-proposal" className="h-9 px-4 gap-1.5"><FileText className="h-3.5 w-3.5" /> {t.proposal}</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="lead-tab-timeline" className="h-9 px-4">{t.timeline}</TabsTrigger>
            <TabsTrigger value="visits" data-testid="lead-tab-visits" className="h-9 px-4">{t.visits} <span className="ml-1 text-xs tabular-nums opacity-80">{lead.visits?.length || 0}</span></TabsTrigger>
            <TabsTrigger value="details" data-testid="lead-tab-details" className="h-9 px-4 lg:hidden">{t.details}</TabsTrigger>
          </TabsList>

          <TabsContent value="simulation" className="p-4 sm:p-6">
            <SimulationPanel lead={lead} onLeadChanged={load} />
          </TabsContent>

          <TabsContent value="measurement" className="p-4 sm:p-6">
            <MeasurementSheet lead={lead} onLeadChanged={load} />
          </TabsContent>

          <TabsContent value="proposal" className="p-4 sm:p-6">
            <ProposalPanel lead={lead} onLeadChanged={load} />
          </TabsContent>

          <TabsContent value="timeline" className="p-4 sm:p-6 space-y-5">
            <div className="flex gap-2">
              <Textarea data-testid="lead-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t.notePlaceholder} rows={2} className="flex-1" onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addNote(); }} />
              <Button onClick={addNote} className="h-auto font-semibold" disabled={!note.trim()} data-testid="lead-note-submit"><Send className="h-4 w-4" /></Button>
            </div>
            <Timeline entries={lead.timeline} onDeleteNote={deleteNote} />
          </TabsContent>

          <TabsContent value="visits" className="p-4 sm:p-6">
            {lead.visits?.length ? (
              <div className="space-y-2" data-testid="lead-visits-list">
                {lead.visits.map((v) => (
                  <div key={v.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3" data-testid={`lead-visit-${v.id}`}>
                    <div className="font-mono text-sm font-semibold">{fmtDate(v.date, lang, { day: '2-digit', month: 'short' })} · {v.time}</div>
                    <VisitTypeBadge type={v.type} />
                    <span className={`text-xs font-semibold ${v.status === 'done' ? 'text-emerald-700' : v.status === 'cancelled' ? 'text-rose-700 line-through' : 'text-amber-700'}`}>{t.visitStatus[v.status]}</span>
                    <span className="text-xs text-muted-foreground">{v.assigned_to_name || v.assigned_to}</span>
                    {v.notes && <span className="text-xs text-muted-foreground italic w-full sm:w-auto">{v.notes}</span>}
                    <div className="ml-auto flex gap-1">
                      {v.status === 'scheduled' && <Button size="sm" variant="outline" className="h-8 text-xs text-emerald-700" onClick={() => setVisitStatus(v, 'done')} data-testid={`visit-done-${v.id}`}>{t.markDone}</Button>}
                      {v.status === 'scheduled' && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setVisitStatus(v, 'cancelled')} data-testid={`visit-cancel-${v.id}`}>{t.markCancelled}</Button>}
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setEditVisit(v); setVisitOpen(true); }} data-testid={`visit-edit-${v.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" onClick={() => deleteVisit(v)} data-testid={`visit-delete-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground py-6 text-center" data-testid="lead-visits-empty">{t.noVisits}</p>}
          </TabsContent>

          <TabsContent value="details" className="p-4 sm:p-6 lg:hidden">
            <LeadDetails lead={lead} t={t} lang={lang} />
          </TabsContent>
        </Tabs>

        <aside className="hidden lg:block bg-card rounded-xl border border-border p-6 shadow-sm h-fit sticky top-20">
          <h3 className="font-display font-bold mb-4">{t.details}</h3>
          <LeadDetails lead={lead} t={t} lang={lang} />
        </aside>
      </div>

      <LeadFormDialog open={editOpen} onOpenChange={setEditOpen} lead={lead} onSaved={load} />
      <VisitFormDialog open={visitOpen} onOpenChange={setVisitOpen} lead={lead} visit={editVisit} onSaved={load} />

      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent className="max-w-sm" data-testid="lead-lost-dialog">
          <DialogHeader><DialogTitle className="font-display">{t.markLost}</DialogTitle></DialogHeader>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t.lostReason}</label>
          <Input data-testid="lead-lost-reason-input" value={lostReason} onChange={(e) => setLostReason(e.target.value)} placeholder={t.lostReasonPlaceholder} className="h-11" />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="h-11" onClick={() => setLostOpen(false)}>{t.cancel}</Button>
            <Button className="h-11 bg-rose-600 hover:bg-rose-700" onClick={async () => { await changeStatus('lost', lostReason); setLostOpen(false); setLostReason(''); }} data-testid="lead-lost-confirm">{t.confirm}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const LeadDetails = ({ lead, t, lang }) => (
  <div className="grid grid-cols-2 lg:grid-cols-1 gap-4" data-testid="lead-details">
    <Info label={t.phone} value={lead.phone} mono />
    <Info label={t.email} value={lead.email} />
    <Info label={t.sourceLabel} value={t.source[lead.source] || lead.source} />
    <Info label={t.estimatedValue} value={fmtMoney(lead.estimated_value, lang)} mono />
    <Info label={t.yearBuilt} value={<span data-testid="lead-year-built">{lead.year_built || '—'}{lead.year_built && lead.year_built < 1978 ? <span className="ml-1.5 inline-flex items-center rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 text-[10px] font-semibold">Lead-Safe</span> : null}</span>} mono />
    <Info label={t.assignedTo} value={lead.assigned_to_name || lead.assigned_to || t.unassigned} />
    {lead.studio_stage && <Info label="Studio" value={<span data-testid="lead-studio-info">{t.studioStage[lead.studio_stage] || lead.studio_stage}{lead.studio_best_time ? ` · ${t.studioTimes[lead.studio_best_time] || lead.studio_best_time}` : ''}{lead.estimate_requested_at ? ` · ${fmtDateTime(lead.estimate_requested_at, lang)}` : ''}</span>} />}
    <Info label={t.createdBy} value={`${lead.created_by_name || lead.created_by} · ${fmtDateTime(lead.created_at, lang)}`} />
    <Info label={t.updatedAt} value={fmtDateTime(lead.updated_at, lang)} />
    {lead.lost_reason && <Info label={t.lostReason} value={lead.lost_reason} />}
    {lead.notes && <div className="col-span-2 lg:col-span-1"><Info label={t.notes} value={<span className="whitespace-pre-wrap">{lead.notes}</span>} /></div>}
    {lead.tags?.length > 0 && <div className="col-span-2 lg:col-span-1 flex flex-wrap gap-1">{lead.tags.map((tg) => <span key={tg} className="inline-flex items-center gap-1 text-[11px] rounded-md bg-muted px-2 py-0.5"><Tag className="h-3 w-3" />{tg}</span>)}</div>}
  </div>
);
